import { apiFetch } from 'packages/lib/api/annotations/rest';
import { ZodType, ZodTypeDef } from 'zod';
import { get, set } from 'idb-keyval';
import { withErrorHandling } from 'packages/lib/utils/error-reporting';

/**
 * Data Mining / Roster Management API Fetcher
 */
const baseApiFetch = apiFetch('annotationsAppApi');

/**
 * A wrapped version of annotationsAppApiFetch that includes error reporting.
 * @param serviceName The name of the service/page reporting the error.
 * @returns A fetcher function with error handling.
 */
export const annotationsAppApiFetch = () => {
  return (
    endpoint: string,
    method?: string,
    body?: BodyInit,
    headers?: HeadersInit,
    showErrorToast?: boolean,
    handleRejection?: (resp: Response) => Promise<Response>
  ): Promise<Response> => {
    return withErrorHandling('Annotations App API', () =>
      baseApiFetch(
        endpoint,
        method,
        body,
        headers,
        showErrorToast,
        handleRejection
      )
    );
  };
};

export const post = async <U, T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  postData: U,
  zodParse: ZodType<T, Z>['parse'],
  responseType: 'text' | 'json' = 'text',
  showErrorToast?: boolean
): Promise<T | null> => {
  const body = JSON.stringify(postData);
  const data = await annotationsAppApiFetch()(
    endpoint,
    'POST',
    body,
    undefined,
    showErrorToast
  ).then((r) => {
    if (r.status === 204) {
      console.log(endpoint + ' status 204 (no content).');
      return null;
    }
    if (responseType === 'text') {
      return r.text();
    }
    return r.json();
  });
  return data ? zodParse(data) : null;
};

export const put = async <T, U, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  data: U,
  zodParse?: ZodType<T, Z>['parse'],
  showErrorToast?: boolean
): Promise<T | null> => {
  const body = JSON.stringify(data);
  const resp = await annotationsAppApiFetch()(
    endpoint,
    'PUT',
    body,
    undefined,
    showErrorToast
  );

  if (resp.status === 204) {
    return null;
  }

  const json = await resp.json();

  if (!zodParse) return json;

  try {
    return zodParse(json);
  } catch (e) {
    console.error('Zod Failed to parse data for endpoint: ', endpoint);
    console.error('  JSON that failed parsing: ', json);
    throw e;
  }
};

export const del = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse?: ZodType<T, Z>['parse'],
  showErrorToast?: boolean
): Promise<T | null> => {
  const resp = await annotationsAppApiFetch()(
    endpoint,
    'DELETE',
    undefined,
    undefined,
    showErrorToast
  );

  if (resp.status === 204) {
    return null;
  }

  const json = await resp.json();

  if (!zodParse) return json;

  try {
    return zodParse(json);
  } catch (e) {
    // Make it easier to debug Zod parse errors
    console.error('Zod Failed to parse data for endpoint: ', endpoint);
    console.error('  JSON that failed parsing: ', json);
    console.error((e as Error).message);
    throw e;
  }
};

/**
 * !important For use with immutable/idempotent responses ONLY
 */
export const postCached = async <U, T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  postData: U,
  zodParse: ZodType<T, Z>['parse'],
  showErrorToast?: boolean
): Promise<T | null> => {
  const body = JSON.stringify(postData);
  const forceCacheBust = window.location.search.includes('cache=none');
  const cacheKey = `${endpoint}.${body}`;
  const cachedData: T | null | undefined = forceCacheBust
    ? null
    : await get(cacheKey).catch((err) => {
        console.warn(err);
        return null;
      });

  const data = cachedData
    ? cachedData
    : await annotationsAppApiFetch()(
        endpoint,
        'POST',
        body,
        undefined,
        showErrorToast
      ).then((r) => {
        if (r.status === 204) {
          console.log(endpoint + ' status 204 (no content).');
          return null;
        }
        return r.text();
      });
  if (!cachedData) {
    if (data) {
      console.log(cacheKey + ' initial cache.');
      void set(cacheKey, data).catch(console.warn);
    } else {
      console.log(cacheKey + ' no data returned.');
    }
  } else {
    console.log(cacheKey + ' cache restored.', cachedData);
  }
  return data ? zodParse(data) : null;
};

/**
 * !important For use with immutable/idempotent assets ONLY
 */
export const apiGetCached = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse?: ZodType<T, Z>['parse'],
  showErrorToast?: boolean,
  overrideHeaders?: HeadersInit
): Promise<T | null> => {
  const forceCacheBust = window.location.search.includes('cache=none');
  const cachedData: T | null | undefined = forceCacheBust
    ? null
    : await get(endpoint).catch((err) => {
        console.warn(err);
        return null;
      });
  const data = cachedData
    ? cachedData
    : await annotationsAppApiFetch()(
        endpoint,
        undefined,
        undefined,
        overrideHeaders,
        showErrorToast
      ).then((r) => {
        if (r.status === 204) {
          console.log(endpoint + ' status 204 (no content).');
          return null;
        }
        return r.json();
      });
  if (!cachedData) {
    if (data) {
      console.log(endpoint + ' initial cache.');
      void set(endpoint, data).catch(console.warn);
    } else {
      console.log(endpoint + ' no data returned.');
    }
  } else {
    console.log(endpoint + ' cache restored.', cachedData);
  }
  if (data) {
    if (!zodParse) {
      return data;
    }
    return zodParse(data);
  }
  return null;
};

export const apiGet = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse?: ZodType<T, Z>['parse'],
  showErrorToast?: boolean,
  overrideHeaders?: HeadersInit
): Promise<T | null> => {
  const resp = await annotationsAppApiFetch()(
    endpoint,
    undefined,
    undefined,
    overrideHeaders,
    showErrorToast
  );
  // For data mining, if a 204 is returned, the client should keep
  // polling to see if the request finishes/publishes.
  if (resp.status === 204) {
    return null;
  }

  const json = await resp.json();

  if (!zodParse) return json;

  try {
    return zodParse(json);
  } catch (e) {
    // Make it easier to debug Zod parse errors
    console.error('Zod Failed to parse data for endpoint: ', endpoint);
    console.error('  JSON that failed parsing: ', json);
    console.error((e as Error).message);
    throw e;
  }
};

export const apiSafeGet = async <T, Z extends ZodTypeDef = ZodTypeDef>(
  endpoint: string,
  zodParse?: ZodType<T, Z>['safeParse'],
  showErrorToast?: boolean,
  overrideHeaders?: HeadersInit
): Promise<T | null> => {
  const resp = await annotationsAppApiFetch()(
    endpoint,
    undefined,
    undefined,
    overrideHeaders,
    showErrorToast
  );

  // For data mining, if a 204 is returned, the client should keep
  // polling to see if the request finishes/publishes.
  if (resp.status === 204) {
    return null;
  }

  const json = await resp.json();

  if (!zodParse) return json;

  try {
    const result = zodParse(json);
    if (!result.success) {
      throw new Error('Zod safeParse failed. Errors: ' + result.error);
    }
    return result.data;
  } catch (e) {
    // Make it easier to debug Zod parse errors
    console.error('Zod Failed to parse data for endpoint: ', endpoint);
    console.error('  JSON that failed parsing: ', json);
    console.error(e);
    throw e;
  }
};
